import { gql, useMutation, useQuery, useReactiveVar } from "@apollo/client";
import * as Sentry from "@sentry/react";
import { useFlags } from "launchdarkly-react-client-sdk";
import moment from "moment-timezone";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import ReactTooltip from "react-tooltip";
import { useDebounce } from "src/utils/hooks";
import styled from "styled-components";

import { AppSelect, AppText } from "src/Components/UI";
import { theme } from "src/utils/theme";
import { BottomFloatingSection } from "./BottomFloatingSection";

import CallModuleV2 from "./CallModuleV2";

import {
  CallFeedbackModal,
  JoinDetailsModal,
  Modal,
  RecordConsentModal,
  TwilioFeedbackModal,
} from "src/Components/modal";
import { currentCallState, inputDevices, loggedInUser, outputDevices, twilioMuted } from "src/apollo/cache";
import { IS_ON_CALL } from "src/apollo/query";
import { CallContext, ModalContext } from "src/context";
import { formatTime } from "src/utils/format";
import { appToast } from "src/utils/toast";

import { VoicemailDropsModule } from "src/Components/Pages/Call/VoicemailDropsModule";
import { FETCH_CONFERENCE_PARTICIPANTS_POLL_INTERVAL } from "src/utils/variables";

const FETCH_LEAD = gql`
  query fetchLead($id: String) {
    fetchLead(id: $id) {
      id
      first_name
      last_name
      full_name
      business_name
      primary_email
      phase
      can_record_without_permission
      sequence_step {
        actions {
          id
          scheduled_event_behavior
        }
      }
      alternate_contacts {
        id
        channel
      }
      associate_parent_id
      next_intent_scheduled_or_unscheduled {
        id
        type
      }
    }
  }
`;

const FETCH_LEADGROUP_NUMBERS = gql`
  query FetchLeadAllAssociatedGroup($fetchLeadAllAssociatedGroupId: String!) {
    fetchLeadAllAssociatedGroup(id: $fetchLeadAllAssociatedGroupId) {
      id
      associate_parent_id
      alternate_contacts {
        id
        channel
      }
      conference_number
    }
  }
`;
const FETCH_CONFERENCE_PARTICIPANTS = gql`
  query fetchConferenceParticipants {
    participants: fetchConferenceParticipants
  }
`;

const UPDATE_ACTIVE_TWILIO_RECORDING = gql`
  mutation updateActiveTwilioRecording($action: TwilioRecordingAction!) {
    updateActiveTwilioRecording(action: $action)
  }
`;

const UPDATE_DEFAULT_AUDIO = gql`
  mutation updateDefaultAudio($default_audio_input: String, $default_audio_output: String) {
    updateDefaultAudio(default_audio_input: $default_audio_input, default_audio_output: $default_audio_output) {
      id
      default_audio_input
      default_audio_output
    }
  }
`;

const FETCH_CALL_NOTES_TEMPLATE_PREVIEW = gql`
  query fetchCallNotesTemplatePreview($lead_id: String!) {
    fetchCallNotesTemplatePreview(lead_id: $lead_id) {
      body
    }
  }
`;

interface CallComponentProps {
  callStartTime?: Date;
  phoneNumber?: string;
  callState?: boolean;
  closeOtherModals?: () => void;
}

const CallModuleSection: React.FC<CallComponentProps> = ({
  callStartTime,
  phoneNumber,
  callState,
  closeOtherModals,
}) => {
  const {
    showDialer,
    toggleDialer,
    showCallFeedbackModal,
    setShowCallFeedbackModal,
    showJoinDetailsModal,
    setShowJoinDetailsModal,
    setShowConsentModal,
  } = useContext(ModalContext);

  const {
    callNotes,
    updateCallNotes,
    recording,
    toggleDidStartRecording,
    startedRecording,
    toggleRecording,
    recordingButtonDisabled,
    persistCallNotes,
    callNotesLastUpdated,
    toggleMute,
    hangup,
    getOutputDevice,
    device,
    changeInputDevice,
    changeOutputDevice,
    isNonCallActivity,
    defaultInput,
    setDefaultInput,
    defaultOutput,
    setDefaultOutput,
    timeOnCall,
    setTimeOnCall,
    deviceResetCheck,
    setDeviceResetCheck,
    setIsConnectingToTwilio,
    isConnectingToTwilio,
    callLeadId,
    callbackStatusAbly,
    setCallbackStatusAbly,
    loadingPlayVoicemailDrop,
    isGoogleMeetCall,
    isZoomCall,
    repPhoneNumber,
    callConnected,
  } = useContext(CallContext);

  const { connectionWarnings, callQualityIcons } = useFlags();

  const [callSettingsModal, setCallSettingsModal] = useState(false);

  const [selectedAudio, setSelectedAudio] = useState<"input" | "output" | undefined>(undefined);

  const [showVoicemailDropsModule, setShowVoicemailDropsModule] = useState(false);
  // incoming ably when rep hits voicemail
  useEffect(() => {
    if (callbackStatusAbly === "voicemail") {
      // user dial in flow (not normal voicemail box)
      if (!!isGoogleMeetCall || !!isZoomCall) return;

      setShowVoicemailDropsModule(true);
    }
  }, [callbackStatusAbly]);

  const { data, loading, error, refetch } = useQuery(FETCH_LEAD, {
    fetchPolicy: "no-cache",
    variables: { id: callLeadId },
    onCompleted: (data) => {
      if (currentCallState().onCall) {
        setIsConnectingToTwilio(false);
        if (data.fetchLead?.can_record_without_permission) {
          appToast(
            "The lead lives in a one-party state",
            {},
            "Recording of the lead and the rep will begin automatically",
          );
          return;
        }
        appToast(
          "This call is not being recorded by default",
          {},
          "This prospect resides in an all-party consent state",
        );
      }
    },
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
    },
  });

  const {
    data: allLeadsData,
    loading: loadingAllLeads,
    error: errorAllLeads,
    refetch: refetchAllLeads,
  } = useQuery(FETCH_LEADGROUP_NUMBERS, {
    fetchPolicy: "network-only",
    variables: {
      fetchLeadAllAssociatedGroupId: callLeadId,
    },
    skip: !callLeadId,
  });

  const leadGroupHasMultipleNumbersToCall = useMemo(() => {
    const primaryLead = allLeadsData?.fetchLeadAllAssociatedGroup?.find((lead: any) => {
      console.log("check in primaryLead lead", lead);
      return lead.associate_parent_id === null;
    });
    const hasAssociatedLeads = allLeadsData?.fetchLeadAllAssociatedGroup?.find((lead: any) => {
      return lead.associate_parent_id !== null;
    });

    const primaryLeadHasAlternatePhoneNumber = primaryLead?.alternate_contacts?.some(
      (contact: any) => contact.channel === "Phone",
    );

    // checks

    if (primaryLeadHasAlternatePhoneNumber) {
      return true;
    }

    if (hasAssociatedLeads) {
      return true;
    }

    return false;
  }, [allLeadsData]);

  const { data: isOnCall, loading: isOnCallLoading } = useQuery(IS_ON_CALL, {
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
    },
  });

  const {
    data: participants,
    refetch: refetchParticipants,
    startPolling,
    stopPolling,
  } = useQuery(FETCH_CONFERENCE_PARTICIPANTS, {
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
    },
  });

  const [initialTemplate, setInitialTemplate] = useState<string>("");

  useQuery(FETCH_CALL_NOTES_TEMPLATE_PREVIEW, {
    fetchPolicy: "network-only",
    variables: { lead_id: callLeadId },
    onCompleted: ({ fetchCallNotesTemplatePreview }) => {
      const templateText = fetchCallNotesTemplatePreview?.body || "";
      setInitialTemplate(templateText);
      updateCallNotes(templateText, callLeadId || "");
    },
    skip: !callLeadId,
  });

  const [updateActiveTwilioRecording] = useMutation(UPDATE_ACTIVE_TWILIO_RECORDING, {
    onCompleted: ({ updateActiveTwilioRecording }) => {
      if (!updateActiveTwilioRecording) {
        appToast("Recording turned off");
        toggleRecording(false);
      } else {
        appToast("Successfully recording");
        toggleRecording(true);
        toggleDidStartRecording(true);
      }
    },
    onError: ({ message }) => {
      Sentry.captureEvent({
        message: `updateActiveTwilioRecording graphql error: ${message}`,
      });
      appToast(`Error updating recording: ${message}`);
    },
  });

  const [updateDefaultAudio] = useMutation(UPDATE_DEFAULT_AUDIO, {
    onCompleted: ({ updateDefaultAudio }) => {
      appToast("Successfully updated default audio");
      selectedAudio === "input" && setDefaultInput(updateDefaultAudio.default_audio_input);
      selectedAudio === "output" && setDefaultOutput(updateDefaultAudio.default_audio_output);
      setSelectedAudio(undefined);
    },
    onError: ({ message }) => {
      console.log("Error in updateDefaultAudio: ", message);
      appToast(message);
      Sentry.captureEvent({
        message: `updateDefaultAudio GraphQL Error: ${message}`,
      });
    },
  });

  const debouncedSaveNotes = useCallback(() => {
    if (!isNonCallActivity && callNotes) {
      // Only auto-save if:
      // 1. Notes aren't empty/whitespace only
      // 2. Notes are different from the initial template
      const trimmedNotes = callNotes.trim();
      const trimmedTemplate = initialTemplate.trim();

      if (trimmedNotes && trimmedNotes !== trimmedTemplate) {
        persistCallNotes();
      }
    }
  }, [callNotes, initialTemplate, isNonCallActivity, persistCallNotes]);

  useDebounce(debouncedSaveNotes, [callNotes], 1000);

  useEffect(() => {
    if (callState) {
      startPolling(FETCH_CONFERENCE_PARTICIPANTS_POLL_INTERVAL);
    } else {
      stopPolling();
    }
    return () => {
      stopPolling();
    };
  }, [callState]);

  const timeOnCallRef = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    const getTimeDiff = (startTime: any) => Math.abs(moment().diff(moment(startTime), "seconds"));
    if (currentCallState().onCall && timeOnCallRef.current) {
      const interval = setInterval(() => {
        const timeDifference = getTimeDiff(callStartTime);
        if (timeOnCallRef.current) {
          timeOnCallRef.current.textContent = formatTime(timeDifference);
        }
        if (deviceResetCheck) {
          setDeviceResetCheck(false);
        }
      }, 1000);

      return () => clearInterval(interval);
    } else {
      setTimeOnCall(getTimeDiff(callStartTime));
    }
  }, [callStartTime, currentCallState().onCall, timeOnCallRef.current]);

  useEffect(() => {
    if (currentCallState().onCall) {
      refetchParticipants();
    }
  }, [currentCallState().onCall]);

  const handleRecordingClick = () => {
    // we need to check that the call is connected before we can start or stop recording
    if (!callConnected) {
      appToast("Please wait for the call to connect before trying to start or stop recording");
      return;
    }

    if (startedRecording) {
      updateActiveTwilioRecording({
        variables: {
          action: recording ? "Pause" : "Resume",
        },
      });
    } else {
      if (!data?.fetchLead?.can_record_without_permission) setShowConsentModal(true);
      else
        updateActiveTwilioRecording({
          variables: {
            action: "Start",
          },
        });
    }
  };

  const isTwilioMuted = useReactiveVar(twilioMuted);

  return (
    <>
      <ReactTooltip
        multiline
        place="top"
        effect="solid"
        id="callbar"
        backgroundColor={theme.PRIMARY800}
        getContent={(dataTip) => (
          <span
            style={{ fontFamily: "Inter", fontStyle: "normal", fontWeight: 600, fontSize: "10px", lineHeight: "14px" }}
          >
            {dataTip}
          </span>
        )}
      />
      {callSettingsModal && (
        <Modal open={callSettingsModal} onClose={() => setCallSettingsModal(false)} closeButtonSize={16}>
          <SettingsModalContainer>
            <TitleDiv>
              <TitleText>Settings</TitleText>
            </TitleDiv>
            <PaddingAndCenterDiv>
              <AppSelect
                name="inputSelect"
                options={inputDevices()}
                appSelectIcon={<BsMicIcon />}
                value={device?.audio?.inputDevice?.deviceId || "default"}
                onChange={(e: any) => {
                  setSelectedAudio("input");
                  updateDefaultAudio({
                    variables: {
                      default_audio_input: e.target.value ?? undefined,
                      default_audio_output: defaultOutput ?? undefined,
                    },
                  });
                  changeInputDevice(e.target.value);
                }}
              />
              <AppSelect
                name="outputSelect"
                options={outputDevices()}
                value={getOutputDevice()}
                onChange={(e: any) => {
                  setSelectedAudio("output");
                  updateDefaultAudio({
                    variables: {
                      default_audio_input: device?.audio?.inputDevice?.deviceId ?? defaultInput ?? undefined,
                      default_audio_output: e.target.value ?? undefined,
                    },
                  });
                  changeOutputDevice(e.target.value);
                }}
                appSelectIcon={<BsVolumeIcon />}
              />
            </PaddingAndCenterDiv>
          </SettingsModalContainer>
        </Modal>
      )}

      {showCallFeedbackModal && (
        <CallFeedbackModal blinds={showCallFeedbackModal} setBlinds={setShowCallFeedbackModal} role="rep" />
      )}
      <TwilioFeedbackModal />

      {callState && currentCallState().onCall && (
        <>
          <RecordConsentModal />
          <JoinDetailsModal />
        </>
      )}

      {callState && (
        <BottomFloatingSection>
          <VoicemailDropsModule
            leadGroupHasMultipleNumbersToCall={leadGroupHasMultipleNumbersToCall}
            showVoicemailDrops={showVoicemailDropsModule && currentCallState().onCall}
            superDialer={true}
          />
          <CallModuleV2
            data={data}
            timeOnCallRef={timeOnCallRef}
            callQualityIcons={callQualityIcons}
            connectionWarnings={connectionWarnings}
            phoneNumber={phoneNumber}
            toggleDialer={toggleDialer}
            toggleMute={toggleMute}
            showDialer={showDialer}
            isTwilioMuted={isTwilioMuted}
            isOnCallLoading={isOnCallLoading}
            isOnCall={isOnCall}
            recording={recording}
            participants={participants}
            refetchParticipants={refetchParticipants}
            handleRecordingClick={handleRecordingClick}
            hangup={() =>
              hangup({
                leaveVoicemailDrop: false,
                tryAlternateContact: false,
              })
            }
            showVoicemailDropsModule={showVoicemailDropsModule}
            setShowVoicemailDropsModule={setShowVoicemailDropsModule}
            showJoinDetailsModal={showJoinDetailsModal}
            setShowJoinDetailsModal={setShowJoinDetailsModal}
            setCallSettingsModal={setCallSettingsModal}
            repProfileImage={loggedInUser()?.profile_image}
          />
        </BottomFloatingSection>
      )}
    </>
  );
};

export default CallModuleSection;

const CallComponentContainer = styled.div`
  width: fit-content;
  height: 100%;
  background: ${theme.surface.neutral.tertiary};
  margin-left: 64px;
`;

const MainContainer = styled.div`
  display: flex;
  position: initial;
  padding-top: 18px;
  padding-bottom: 24px;
  margin: initial;
  height: 100%;
  background: ${theme.surface.neutral.tertiary};
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  overflow: auto;
  min-height: 100%;
  width: fit-content;
`;

const SettingsModalContainer = styled.div`
  height: 226px;
  width: 432px;
  background-color: ${theme.text.neutral.inverse};
`;

const TitleText = styled(AppText)`
  font-weight: 600;
  font-size: 14px;
  line-height: 21px;
`;

const TitleDiv = styled.div`
  position: relative;
  height: 56px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.NEUTRAL100};
  border-bottom: 1px solid ${theme.NEUTRAL200};
`;

const PaddingAndCenterDiv = styled.div`
  padding: 24px;
  padding-bottom: 14px;
`;

const BsMicIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  &::before {
    content: "🎤";
  }
`;

const BsVolumeIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  &::before {
    content: "🔊";
  }
`;
